/**
 * Florida, one clickable path per county.
 *
 * The shapes are generated from the US Census cartographic boundary file
 * (tools/generate_florida_map.py) and carry the same FIPS codes the backend
 * registry uses, so a county drawn here and a county the API will filter by are
 * the same thing by construction rather than by a name lookup.
 *
 * A county that carries no data is drawn, labelled and focusable — it is just
 * not a link. Hiding the 66 counties we do not cover would make the map a
 * picture of Miami-Dade and quietly drop the point it exists to make.
 */

import type { County } from "../api/types";
import { FLORIDA_COUNTY_SHAPES, FLORIDA_VIEWBOX } from "../lib/floridaShapes";

interface Props {
  /** Keyed by FIPS. A shape with no entry renders as uncovered. */
  counties: Record<string, County>;
  selected: string;
  onSelect: (fips: string) => void;
}

export function FloridaMap({ counties, selected, onSelect }: Props) {
  return (
    <svg
      className="fl-map"
      viewBox={FLORIDA_VIEWBOX}
      role="group"
      aria-label="Florida counties, by coverage"
    >
      {FLORIDA_COUNTY_SHAPES.map((shape) => {
        const county = counties[shape.fips];
        const status = county?.status ?? "not_covered";
        const live = status === "live";
        const isSelected = selected === shape.fips;

        return (
          <g key={shape.fips}>
            <path
              d={shape.d}
              className={`fl-county is-${status}${isSelected ? " is-selected" : ""}`}
              // Only live counties are interactive: a button that does nothing
              // is worse than a shape that never claimed to be one.
              role={live ? "button" : undefined}
              tabIndex={live ? 0 : undefined}
              aria-pressed={live ? isSelected : undefined}
              aria-label={ariaLabel(shape.name, county)}
              onClick={live ? () => onSelect(shape.fips) : undefined}
              onKeyDown={
                live
                  ? (event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      onSelect(shape.fips);
                    }
                  : undefined
              }
            >
              {/* Every county names itself on hover. Sixty-seven printed
                  labels at this size would be unreadable, and a map where
                  only the covered county is named reads as a map of one
                  county. */}
              <title>{ariaLabel(shape.name, county)}</title>
            </path>
            {status !== "not_covered" && (
              <text
                className="fl-label"
                x={shape.label[0]}
                y={shape.label[1]}
                textAnchor="middle"
                fontSize={labelSize(shape.name, shape.room)}
              >
                {shape.name}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** Roughly how wide one character is, as a fraction of the type size. */
const CHARACTER_WIDTH = 0.58;
const MAX_LABEL = 13;
const MIN_LABEL = 7.5;

/**
 * Type size that keeps a county's name inside the county.
 *
 * `room` is the shape's horizontal extent at the label's own height, measured
 * when the map was generated — not the bounding box, which for a coastal
 * county is mostly ocean and lets the name run out over the water.
 *
 * Below MIN_LABEL the name would be unreadable anyway, so it is allowed to
 * overhang a little rather than shrink into nothing.
 */
function labelSize(name: string, room: number): number {
  const ideal = (room * 0.9) / (name.length * CHARACTER_WIDTH);
  return Math.max(MIN_LABEL, Math.min(MAX_LABEL, ideal));
}

function ariaLabel(name: string, county?: County): string {
  if (!county || county.status === "not_covered") {
    return `${name} County — not covered`;
  }
  if (county.status === "planned") return `${name} County — planned`;
  return `${name} County — ${county.buildings.toLocaleString()} buildings`;
}
